<template>
  <BarLoader />
</template>

<script lang="ts">
import { defineComponent, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { gql } from "@apollo/client/core";
import { useQuery } from "@vue/apollo-composable";
import { useFrontegg } from "@frontegg/vue";
import { CreateExpertProgram } from "@/types/user-store-types";
import { CompanyUserExpertProgram } from "@/types/expert-program-types";
import BarLoader from "@/components/shared/BarLoader.vue";

const GET_COMPANY_USER = gql`
  query getCompanyUser {
    getCompanyUser {
      id
      email
      firstName
      lastName
      profilePicture
      company {
        id
        name
        website
      }
      uuid
      expertProgram {
        id
        uuid
        name
        website
        onboardingStep
      }
    }
  }
`;

export default defineComponent({
  name: "SignInLanding",
  components: {
    BarLoader,
  },

  setup() {
    const router = useRouter();
    const userStore = useUserStore();
    const { loginWithRedirect } = useFrontegg();

    onMounted(() => {
      if (userStore.token) {
        userStore.setUserType("company_user");
        fetchCompanyUser();
      } else {
        handleError("No token found.");
      }
    });

    const produktlyIdentifyUserLogin = (uuid: string, email: string) => {
      if (window.Produktly) {
        window.Produktly.identifyUser(uuid, {
          email: email,
        });
      }
    };

    const fetchCompanyUser = async () => {
      const { result: companyUserResult, error: companyUserError } =
        useQuery(GET_COMPANY_USER);

      watch(
        () => companyUserResult.value,
        (newValue) => {
          if (newValue) {
            const companyUser = newValue.getCompanyUser;
            produktlyIdentifyUserLogin(companyUser.uuid, companyUser.email);
            userStore.setEmail(companyUser.email);
            userStore.setFirstName(companyUser.firstName);
            userStore.setLastName(companyUser.lastName);
            userStore.setCompanyName(companyUser.company.name);
            userStore.setCompanyWebsite(companyUser.company.website);
            userStore.setCompanyId(companyUser.company.id);
            userStore.setProfilePicture(companyUser.profilePicture);
            determineLandingPage(companyUser?.expertProgram);
          } else {
            handleError("No company user found");
          }
        }
      );

      watch(
        () => companyUserError.value,
        (newError) => {
          if (newError) {
            handleError(newError.message);
          }
        }
      );
    };

    const determineLandingPage = (
      expertProgram: CompanyUserExpertProgram | null
    ) => {
      if (expertProgram) {
        if (expertProgram.onboardingStep === "completed") {
          router.push({ name: "ExpertPrograms" });
        } else {
          setProgramCreationDetails(expertProgram);
          if (expertProgram.onboardingStep === "billing") {
            router.push({ name: "AddAccountBalance" });
          } else if (expertProgram.onboardingStep === "integration") {
            router.push({ name: "AddIntegration" });
          }
        }
      } else {
        router.push({ name: "AddProgramDetails" });
      }
    };

    const setProgramCreationDetails = (
      expertProgram: CompanyUserExpertProgram
    ) => {
      const createExpertProgram: CreateExpertProgram = {
        id: expertProgram.id,
        uuid: expertProgram.uuid,
        name: expertProgram.name,
        website: expertProgram.website,
        companyId: "",
        directoryCreated: false,
        widgetCreated: false,
        crmSynced: false,
        directoryButtonStyles: {
          backgroundColor: "",
          textColor: "",
          boldness: "",
          fontFamily: "",
          fontSize: "",
        },
        createdWidgetIds: [],
      };
      userStore.setCreateExpertProgram(createExpertProgram);
    };

    const handleError = (error: string) => {
      loginWithRedirect();
    };
  },
});
</script>
